// item 1544 (Chad) — SUBSCRIPTION ENHANCEMENTS: a tenant-defined list (name + detailed description)
// kept in Settings → RNWY and stored as JSON under the Rnwy.SubscriptionEnhancements settings key.
// Every RNWY proposal and every subscription lists the ACTIVE ones as subscription-only extras —
// included with the sub, never priced. Archived rows stay in the JSON (history) and never show.
// item 1546 (Chad: "add these enhancements as they're in beta, as well as future ones") — each row
// also carries a release status and which licenses carry it. A pre-1546 row (no fields) reads as
// live + subscription-only, exactly what 1544 meant. Mirrors RnwySubscriptionEnhancement (backend).

// item 1553 (Daniel via Chad) — 'coming' is retired: an unreleased item is added and left DISABLED
// until it reaches beta, so nothing "coming" ever prints. A legacy 'coming' row reads as beta + disabled.
export type EnhancementStatus = 'live' | 'beta';
export type EnhancementAppliesTo = 'subscription' | 'all';

export interface RnwySubscriptionEnhancement {
  id: string;
  name: string;
  description: string;
  archived?: boolean;
  enabled: boolean; // item 1553 — off = never on proposals, PDFs, the approval page or contracts
  status: EnhancementStatus;
  appliesTo: EnhancementAppliesTo;
}

const rawStatus = (v: unknown) => String(v ?? '').trim().toLowerCase();

export const normalizeStatus = (v: unknown): EnhancementStatus => {
  const s = rawStatus(v);
  return s === 'beta' || s === 'coming' ? 'beta' : 'live';
};

// item 1553 — explicit flag wins; a row saved before 1553 is enabled unless it was 'coming'
export const normalizeEnabled = (enabled: unknown, status: unknown): boolean => (
  typeof enabled === 'boolean' ? enabled : rawStatus(status) !== 'coming'
);

export const normalizeAppliesTo = (v: unknown): EnhancementAppliesTo => (
  String(v ?? '').trim().toLowerCase() === 'all' ? 'all' : 'subscription'
);

// Settings picker labels
export const STATUS_OPTIONS: { value: EnhancementStatus; label: string }[] = [
  { value: 'live', label: 'Available' },
  { value: 'beta', label: 'Beta' },
];
// item 1547 (Chad, epic EPIC_LICENSE_FEATURES_2026-10 §3) — the "Applies to" dropdown is gone:
// Settings › RNWY shows TWO sections and the section a row lives in IS its placement.
//   'all'          → Section A: Perpetual & Subscription · included with every license
//   'subscription' → Section B: Subscription enhancements
export const SECTION_TITLES: Record<EnhancementAppliesTo, string> = {
  all: 'Perpetual & Subscription · included with every license',
  subscription: 'Subscription enhancements',
};

// the client-facing tag ('' for live) — same words as the PDF
export const statusTag = (s: EnhancementStatus): string => (s === 'beta' ? 'BETA' : '');

export const parseEnhancements = (json?: string | null): RnwySubscriptionEnhancement[] => {
  if (!json) return [];
  try {
    const raw = JSON.parse(json);
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((r) => r && typeof r === 'object')
      .map((r) => ({
        id: String(r.id ?? r.Id ?? ''),
        name: String(r.name ?? r.Name ?? ''),
        description: String(r.description ?? r.Description ?? ''),
        archived: !!(r.archived ?? r.Archived),
        enabled: normalizeEnabled(r.enabled ?? r.Enabled, r.status ?? r.Status),
        status: normalizeStatus(r.status ?? r.Status),
        appliesTo: normalizeAppliesTo(r.appliesTo ?? r.AppliesTo),
      }));
  } catch {
    return [];
  }
};

export const serializeEnhancements = (rows: RnwySubscriptionEnhancement[]): string => JSON.stringify(
  rows.map((r) => ({
    id: r.id,
    name: r.name.trim(),
    description: r.description.trim(),
    archived: !!r.archived,
    enabled: r.enabled,
    status: r.status,
    appliesTo: r.appliesTo,
  })),
);

// what prints: not archived, ENABLED (item 1553), and named
export const activeEnhancements = (json?: string | null) => parseEnhancements(json)
  .filter((e) => !e.archived && e.enabled && e.name.trim().length > 0);

export const newEnhancementId = () => `enh-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

// item 1547 — the rows of one Settings section, in print order (the array order IS the print order)
export const rowsInSection = (rows: RnwySubscriptionEnhancement[], section: EnhancementAppliesTo) => rows
  .filter((r) => r.appliesTo === section);

// item 1547 — move a row up (-1) / down (+1) among the rows of ITS OWN section (other section's
// rows in between are skipped, so they never move)
export const moveInSection = (rows: RnwySubscriptionEnhancement[], id: string, dir: -1 | 1) => {
  const i = rows.findIndex((r) => r.id === id);
  if (i < 0) return rows;
  let j = i + dir;
  while (j >= 0 && j < rows.length && rows[j].appliesTo !== rows[i].appliesTo) j += dir;
  if (j < 0 || j >= rows.length) return rows;
  const next = [...rows];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

// item 1547 — re-place a row in the other section (Chad's call, e.g. a Subscription enhancement
// later becomes an every-license feature); it lands LAST in its new section. Papers already
// generated never change — their text is frozen at generation.
export const switchSection = (rows: RnwySubscriptionEnhancement[], id: string) => {
  const row = rows.find((r) => r.id === id);
  if (!row) return rows;
  const moved: RnwySubscriptionEnhancement = { ...row, appliesTo: row.appliesTo === 'all' ? 'subscription' : 'all' };
  return [...rows.filter((r) => r.id !== id), moved];
};

// item 1549 — the ACTIVE rows split by section, for every read-only surface
export const activeBySection = (json?: string | null) => {
  const active = activeEnhancements(json);
  return { everyLicense: rowsInSection(active, 'all'), subscription: rowsInSection(active, 'subscription') };
};
